import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import {
  login as loginAction,
  logout as logoutAction,
  getUser as getUserAction,
  changeSessionTenant as changeSessionTenantAction,
  selectRole as selectRoleAction,
  updateUserTenant as updateUserTenantAction
} from '../redux/authSlice';

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const state = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const login = useCallback(async (data) => {
    return unwrapResult(await dispatch(loginAction(data)));
  }, []);

  const logout = useCallback(async () => {
    dispatch(logoutAction());
  }, []);

  const updateUserTenant = useCallback((data) => {
    dispatch(updateUserTenantAction(data));
  }, []);

  const changeSessionTenant = useCallback(async (tenantId) => {
    return unwrapResult(await dispatch(changeSessionTenantAction(tenantId)));
  }, []);

  const getUser = useCallback(async () => {
    return unwrapResult(await dispatch(getUserAction()));
  }, []);

  const selectRole = useCallback((role) => {
    dispatch(selectRoleAction(role));
  }, []);

  const selectedTenant = useMemo(() => {
    return state.user?.tenants?.find(
      (t) => t.id === state.user?.selectedTenant
    );
  }, [state.user?.tenants, state.user?.selectedTenant]);

  const authLevel = useMemo(() => {
    return [
      ...(state.user?.roles || []),
      ...(selectedTenant?.roles || [])
    ].reduce((level, role) => {
      return Math.min(level, role.level);
    }, 1000);
  }, [state.user?.roles, selectedTenant?.roles, state.selectedRole]);

  useEffect(() => {
    getUser().catch(() => null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        role: state.role,
        loading: state.loading,
        isLoggedIn: state.isLoggedIn,
        tenantId: state.tenantId,
        selectedRole: state.selectedRole,
        selectedTenant,
        authLevel,
        login,
        logout,
        getUser,
        changeSessionTenant,
        selectRole,
        updateUserTenant
      }}>
      {children}
    </AuthContext.Provider>
  );
}
